import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { number, object, string } from "yup";

import {
  Icon,
  Modal,
  TextInput,
  Tooltip,
  addToast,
} from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { useKeystone, usePolicyDetail } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { logException, trackEvent } from "utils";

import styles from "./edit-mortgagee-modal.module.scss";
import { ManualMortgagee } from "./manual-mortgagee";
import { MortgageeSearch } from "./mortgagee-search";

const ERROR_DURATION = 10000; // ten seconds

const schema = object().shape({
  name: string().required("Please enter a valid mortgage company name"),
  loanNumber: string().required("Please enter a valid loan number"),
  street1: string().required("Please enter a valid address"),
  street2: string().optional(),
  city: string()
    .required("Please enter a valid city name")
    .matches(/^[a-zA-Z ]*$/, "Please enter a valid city name"),
  state: string().required("Please select a valid state"),
  zip: string()
    .required("Please enter a valid zip code")
    .matches(/^(?:\d{5})?$/, "Please enter a valid zip code"),
  index: number().required(),
});

export type EditMortgageeModalFields = {
  name: string;
  loanNumber: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  index: number;
};

export const EditMortgageeModal = ({
  open,
  onClose,
  keystonePolicyId,
  index,
}) => {
  const keystone = useKeystone();
  const { mutate: refreshPolicyDetail } = usePolicyDetail();
  const [manualForm, setManualForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<EditMortgageeModalFields>({
    defaultValues: {
      name: "",
      loanNumber: "",
      street1: "",
      city: "",
      state: "",
      zip: "",
      index,
    },
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const onCloseModal = () => {
    onClose();
    setManualForm(false);
    reset();
  };

  const onSave = async (updatedMortgagee) => {
    setSubmitting(true);
    const res = await keystone.updateMortgagee({
      keystonePolicyId,
      mortgagee: updatedMortgagee,
    });

    if (isError(res)) {
      logException(res);
      trackEvent("Mortgagee update error caught", {
        errorMessage: res.message,
      });
      addToast("Something went wrong while updating the mortgagee.", {
        icon: true,
        error: true,
        duration: ERROR_DURATION,
      });
    } else {
      refreshPolicyDetail();
      onCloseModal();
      addToast("The mortgagee information has been successfully updated.", {
        icon: true,
      });
      trackEvent("Update Mortgagee", {
        "Mortgagee Name": updatedMortgagee.name,
        "Loan Number": updatedMortgagee.loanNumber,
      });
    }
    setSubmitting(false);
  };

  return (
    <Modal
      open={open}
      onClose={onCloseModal}
      heading="Edit mortgage company"
      primaryAction="Save"
      disablePrimary={!isValid}
      onPrimaryAction={handleSubmit(onSave)}
      loadingPrimary={submitting}
    >
      <Stack>
        {manualForm ? (
          <ManualMortgagee
            register={register}
            setValue={setValue}
            errors={errors}
          />
        ) : (
          <MortgageeSearch
            setValue={setValue}
            onAddMyOwn={() => {
              reset();
              setManualForm(true);
            }}
          />
        )}
        <TextInput
          {...register("loanNumber")}
          label={
            <div className={styles.tooltipContent}>
              Loan number
              <Tooltip content="Find your loan number on your mortgage statement, online account, or closing documents.">
                <Icon name="InformationSubtle" size="sm" />
              </Tooltip>
            </div>
          }
          errorMessage={errors.loanNumber?.message}
          isError={!!errors.loanNumber}
        />
      </Stack>
    </Modal>
  );
};
